import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Avatar,
  Tooltip,
  Alert,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  DeleteOutline as ClearIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { ragAPI } from '../../services/api';

const STORAGE_KEY = 'admin_chatbot_messages';
const MODEL_KEY = 'admin_chatbot_model';

const DEFAULT_MESSAGES = [
  {
    id: '1',
    text: 'नमस्कार! मी तुमची मदतनीस आहे. मी तुम्हाला HBNC आणि HBYC फॉर्म्स बद्दल मदत करू शकते.',
    sender: 'bot',
    timestamp: new Date().toISOString(),
  },
  {
    id: '2',
    text: 'तुम्हाला काय माहिती हवी आहे?',
    sender: 'bot',
    timestamp: new Date().toISOString(),
  },
];

const MODELS = [
  { value: 'rag',    label: 'GPT-4o-mini', color: 'primary' },
  { value: 'onprem', label: 'Mistral 7B',  color: 'secondary' },
  { value: 'vllm',   label: 'Mistral-7B-Instruct-v0.2-AWQ (vLLM)', color: 'warning' },
];

function formatTime(isoString) {
  return new Date(isoString).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function ModelChip({ modelValue }) {
  const m = MODELS.find(x => x.value === modelValue);
  if (!m) return null;
  const colorMap = {
    primary:   { bg: '#EFF6FF', text: '#2563EB' },
    secondary: { bg: '#F5F3FF', text: '#7C3AED' },
    warning:   { bg: '#FFFBEB', text: '#D97706' },
  };
  const c = colorMap[m.color];
  return (
    <Chip
      label={m.label}
      size="small"
      sx={{
        height: 14, fontSize: '0.6rem',
        bgcolor: c.bg, color: c.text,
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
}

function MessageBubble({ message }) {
  const isUser = message.sender === 'user';
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: 1, mb: 1.5 }}>
      {!isUser && (
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.lighter', border: '1px solid', borderColor: 'primary.light', flexShrink: 0 }}>
          <BotIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        </Avatar>
      )}
      <Box sx={{ maxWidth: '70%' }}>
        <Box sx={{
          px: 2, py: 1.25,
          borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          bgcolor: isUser ? 'primary.main' : 'background.paper',
          border: isUser ? 'none' : '1px solid', borderColor: 'grey.200',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}>
          <Typography variant="body2" sx={{ color: isUser ? '#fff' : 'grey.800', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {message.text}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
          {!isUser && message.model && <ModelChip modelValue={message.model} />}
          <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.7rem' }}>
            {formatTime(message.timestamp)}
            {message.mode === 'offline' && ' · ऑफलाइन'}
          </Typography>
        </Box>
      </Box>
      {isUser && (
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', flexShrink: 0 }}>
          <PersonIcon sx={{ fontSize: 18 }} />
        </Avatar>
      )}
    </Box>
  );
}

function TypingIndicator() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 1.5 }}>
      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.lighter', border: '1px solid', borderColor: 'primary.light', flexShrink: 0 }}>
        <BotIcon sx={{ fontSize: 18, color: 'primary.main' }} />
      </Avatar>
      <Box sx={{ px: 2, py: 1.5, borderRadius: '16px 16px 16px 4px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'grey.200', display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <CircularProgress size={8} thickness={6} sx={{ color: 'grey.400' }} />
        <Typography variant="caption" sx={{ color: 'grey.500' }}>उत्तर शोधत आहे...</Typography>
      </Box>
    </Box>
  );
}

function Chatbot() {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [model, setModel] = useState(() => localStorage.getItem(MODEL_KEY) || 'rag');
  const [vllmModel, setVllmModel] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Load saved messages
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setMessages(parsed.length > 0 ? parsed : DEFAULT_MESSAGES);
      } catch {
        setMessages(DEFAULT_MESSAGES);
      }
    } else {
      setMessages(DEFAULT_MESSAGES);
    }

    // Auto-discover vLLM model from /v1/models endpoint
    const vllmUrl = process.env.REACT_APP_VLLM_URL;
    if (vllmUrl) {
      const modelsUrl = vllmUrl.replace('/chat/completions', '/models');
      axios.get(modelsUrl, {
        headers: { Authorization: `Bearer ${process.env.REACT_APP_VLLM_API_KEY}` },
      }).then(res => {
        const firstModel = res.data?.data?.[0]?.id;
        if (firstModel) setVllmModel(firstModel);
      }).catch(() => {}); // silent fail — vLLM may not be running
    }
  }, []);

  useEffect(() => {
    if (messages.length > 0) localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleModelChange = (e) => {
    setModel(e.target.value);
    localStorage.setItem(MODEL_KEY, e.target.value);
  };

  const handleSend = async () => {
    const query = inputText.trim();
    if (!query || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      text: query,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => {
      const filtered = prev.filter(m => m.id !== '1' && m.id !== '2');
      return [...filtered, userMessage];
    });
    setInputText('');
    setIsLoading(true);
    setError('');

    try {
      let answerText;

      if (model === 'rag') {
        // ZP backend RAG (OpenAI default) — FAISS retrieval + LLM
        const res = await ragAPI.query(query);
        answerText = res.data.answer;

      } else if (model === 'onprem') {
        // ZP backend RAG with on-prem Ollama as LLM
        const res = await ragAPI.query(query, process.env.REACT_APP_ONPREM_LLM_MODEL);
        answerText = res.data.answer;

      } else if (model === 'vllm') {
        // ZP backend RAG with vLLM as LLM
        const res = await ragAPI.query(query, vllmModel || 'vllm');
        answerText = res.data.answer;
      }

      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        text: answerText,
        sender: 'bot',
        timestamp: new Date().toISOString(),
        model,
      }]);

    } catch (err) {
      const modelLabel = MODELS.find(m => m.value === model)?.label || model;
      const errorMsg = err.response?.status === 401
        ? 'Session expired. Please log in again.'
        : `माफ करा, उत्तर मिळवताना त्रुटी आली (${modelLabel}). कृपया पुन्हा प्रयत्न करा.`;
      setError(errorMsg);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleClear = () => {
    setMessages(DEFAULT_MESSAGES);
    localStorage.removeItem(STORAGE_KEY);
    setError('');
  };

  return (
    <Box sx={{ height: 'calc(100vh - 110px)', display: 'flex', flexDirection: 'column', maxWidth: 800, mx: 'auto' }}>
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0 }}>

        {/* Messages */}
        <Box sx={{
          flex: 1, overflowY: 'auto', p: 2.5, bgcolor: 'grey.50',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 2 },
        }}>
          {messages.map(msg => <MessageBubble key={msg.id} message={msg} />)}
          {isLoading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </Box>

        {/* Error */}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mx: 2, mb: 1, borderRadius: 1 }}>
            {error}
          </Alert>
        )}

        {/* Model Selector */}
        <Box sx={{
          px: 2, py: 0.75,
          borderTop: '1px solid', borderColor: 'grey.100',
          bgcolor: 'grey.50',
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <Typography variant="caption" sx={{ color: 'grey.500', flexShrink: 0, fontSize: '0.7rem' }}>
            Model:
          </Typography>
          <Select
            value={model}
            onChange={handleModelChange}
            size="small"
            sx={{ fontSize: '0.75rem', height: 28, minWidth: 150, bgcolor: 'background.paper' }}
          >
            {MODELS.map(m => (
              <MenuItem key={m.value} value={m.value} sx={{ fontSize: '0.75rem' }}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Input */}
        <Box sx={{
          p: 2, borderTop: '1px solid', borderColor: 'grey.200',
          bgcolor: 'background.paper', display: 'flex', gap: 1, alignItems: 'flex-end',
        }}>
          <Tooltip title="संभाषण साफ करा">
            <IconButton onClick={handleClear} size="small" sx={{ color: 'grey.400', flexShrink: 0, '&:hover': { color: 'error.main', bgcolor: 'error.lighter' } }}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
          <TextField
            inputRef={inputRef}
            fullWidth multiline maxRows={4}
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="संदेश टाइप करा... (Enter to send)"
            disabled={isLoading}
            variant="outlined" size="small"
            inputProps={{ maxLength: 500 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: 'grey.50', '&.Mui-focused': { bgcolor: 'background.paper' } } }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!inputText.trim() || isLoading}
            sx={{ bgcolor: 'primary.main', color: '#fff', width: 40, height: 40, flexShrink: 0, '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.400' } }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Card>
    </Box>
  );
}

export default Chatbot;
